import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Mail, Globe } from "lucide-react";
import BrandWordmark from "./BrandWordmark";

/**
 * PublicFooter — shared footer for public pages.
 *
 * Two modes:
 *   • Full (default)  → marketing-style footer with brand, parent-company
 *                       attribution, contact, and CTAs. Use on landing &
 *                       segment marketing pages.
 *   • compact         → just the thin legal strip (© + Privacy + Terms).
 *                       Use on transactional pages (job detail, verify,
 *                       policy pages, signup, LMS reader) where the big
 *                       marketing block adds noise.
 */
export default function PublicFooter({ compact = false }) {
  const year = new Date().getFullYear();

  if (compact) {
    return (
      <footer className="border-t border-rule bg-white" data-testid="public-footer-compact">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
          <div>
            © {year}{" "}
            <a href="https://techcatalyst.world/" target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-signal transition">
              Tech Catalyst Private Limited
            </a>
            . SkillConnect™ is a product of Tech Catalyst.
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/legal" className="hover:text-signal transition" data-testid="footer-legal-compact">Legal</Link>
            <span>•</span>
            <Link to="/legal/privacy" className="hover:text-signal transition" data-testid="footer-privacy">Privacy</Link>
            <span>•</span>
            <Link to="/legal/terms" className="hover:text-signal transition" data-testid="footer-terms">Terms</Link>
            <span>•</span>
            <Link to="/legal/grievance" className="hover:text-signal transition" data-testid="footer-grievance-compact">Grievance</Link>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-rule bg-white" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-6 py-12 md:py-14">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Brand + attribution */}
          <div className="md:col-span-7">
            <Link to="/" className="inline-flex items-center" aria-label="SkillConnect home">
              <BrandWordmark to={null} size={36} testId="footer-brand-wordmark" />
            </Link>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-md">
              SkillConnect is an integrated CRM and placement-management platform
              for employers, university admins, students, and working professionals — built on verified
              skills, smart matching, and measurable outcomes.
            </p>

            <a
              href="https://techcatalyst.world/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-techcatalyst-link"
              className="mt-6 inline-flex items-center gap-5 rounded-2xl border border-rule p-5 bg-paper hover:shadow-lg transition-shadow"
            >
              <div className="w-32 h-32 rounded-xl bg-white shrink-0 ring-1 ring-rule p-4 overflow-hidden flex items-center justify-center">
                <img
                  src="/brand/techcatalyst-logo.png"
                  alt="Tech Catalyst Private Limited"
                  className="w-full h-full object-contain"
                  draggable="false"
                />
              </div>
              <div className="leading-tight">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
                  A product by
                </div>
                <div
                  className="font-display font-bold text-lg inline-flex items-center gap-1.5 mt-1 hover:text-signal transition"
                  style={{ color: "var(--ink)" }}
                >
                  Tech Catalyst Private Limited
                  <ExternalLink size={15} />
                </div>
                <div className="text-xs text-slate-500 mt-1">techcatalyst.world</div>
              </div>
            </a>
          </div>

          {/* Company */}
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
              Connect
            </div>
            <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
              <li className="inline-flex items-center gap-2">
                <Globe size={14} className="text-signal" />
                <a href="https://techcatalyst.world/" target="_blank" rel="noopener noreferrer" className="hover:text-signal transition">
                  techcatalyst.world
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <Globe size={14} className="text-signal" />
                <span>skillconnect.com</span>
              </li>
              <li className="inline-flex items-center gap-2">
                <Mail size={14} className="text-signal" />
                <a href="mailto:info@skillconnect.com" className="hover:text-signal transition">info@skillconnect.com</a>
              </li>
            </ul>
            <div className="mt-6 flex gap-2">
              <Link
                to="/signup?role=student"
                className="px-4 py-2 rounded-md text-xs font-semibold text-white transition shadow-sm hover:shadow-md"
                style={{ background: "linear-gradient(135deg,#006CFF 0%,#0040A8 100%)" }}
                data-testid="footer-cta-signup"
              >
                Get started
              </Link>
              <Link
                to="/login"
                className="px-4 py-2 rounded-md text-xs font-semibold border border-rule hover:bg-slate-50 transition"
                data-testid="footer-cta-login"
              >
                Log in
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-rule flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs text-slate-500">
          <div>
            © {year}{" "}
            <a
              href="https://techcatalyst.world/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:text-signal transition"
            >
              Tech Catalyst Private Limited
            </a>
            . All rights reserved. SkillConnect™ is a product of Tech Catalyst.
          </div>
          <div className="flex gap-3 flex-wrap">
            <a href="https://techcatalyst.world/" target="_blank" rel="noopener noreferrer" className="hover:text-signal transition">Parent company</a>
            <span>•</span>
            <Link to="/legal" className="hover:text-signal transition" data-testid="footer-legal">Legal &amp; policies</Link>
            <span>•</span>
            <Link to="/legal/privacy" className="hover:text-signal transition" data-testid="footer-privacy">Privacy</Link>
            <span>•</span>
            <Link to="/legal/terms" className="hover:text-signal transition" data-testid="footer-terms">Terms</Link>
            <span>•</span>
            <Link to="/legal/grievance" className="hover:text-signal transition" data-testid="footer-grievance">Grievance</Link>
            <span>•</span>
            <Link to="/legal/refunds" className="hover:text-signal transition" data-testid="footer-refunds">Refunds</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
